import * as React from 'react';
import {
    Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, Stack, Typography,
} from '@mui/material';
import { importVirtualTeams } from '../../Api';
import { VirtualTeamImportResultDto } from '../../models/dtos/VirtualProviderCatalogDto';

const REQUIRED_HEADERS = [
    'sportId',
    'name',
    'nameRu',
    'description',
    'descriptionRu',
    'history',
    'historyRu',
    'originCountry',
    'svg',
];

export default function VirtualTeamImportDialog(props: {
    open: boolean;
    onClose: () => void;
    onImported: (result: VirtualTeamImportResultDto) => void;
}) {
    const [file, setFile] = React.useState<File | null>(null);
    const [csv, setCsv] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!props.open) return;
        setFile(null);
        setCsv('');
        setError('');
    }, [props.open]);

    const selectFile = async (selected: File | undefined) => {
        setFile(selected ?? null);
        setCsv('');
        setError('');
        if (!selected) return;
        if (!selected.name.toLowerCase().endsWith('.csv')) {
            setError('Select a CSV file.');
            return;
        }
        if (selected.size > 20_000_000) {
            setError('CSV files cannot exceed 20 MB.');
            return;
        }
        try {
            setCsv(await selected.text());
        } catch {
            setError('Could not read the selected CSV file.');
        }
    };

    const importTeams = async () => {
        setBusy(true);
        setError('');
        try {
            const result = await importVirtualTeams(csv);
            props.onImported(result);
            props.onClose();
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Team import failed');
        } finally {
            setBusy(false);
        }
    };

    return <Dialog open={props.open} onClose={busy ? undefined : props.onClose} fullWidth maxWidth="md">
        <DialogTitle>Import virtual teams</DialogTitle>
        <DialogContent>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            <Stack spacing={2} sx={{ mt: 1 }}>
                <DialogContentText>
                    Upload a UTF-8 CSV containing team identity, bilingual description and history,
                    origin country, and SVG logo. The server allocates players from the existing free
                    pool. Every row is validated before the import is committed.
                </DialogContentText>
                <Typography variant="caption" component="code" sx={{ overflowWrap: 'anywhere' }}>
                    {REQUIRED_HEADERS.join(',')}
                </Typography>
                <Button component="label" variant="outlined" disabled={busy} sx={{ alignSelf: 'flex-start' }}>
                    Select CSV file
                    <input
                        hidden
                        type="file"
                        accept=".csv,text/csv"
                        onChange={event => {
                            const selected = event.target.files?.[0];
                            event.target.value = '';
                            void selectFile(selected);
                        }}
                    />
                </Button>
                <Typography color={file ? 'text.primary' : 'text.secondary'}>
                    {file ? `${file.name} (${Math.ceil(file.size / 1024)} KB)` : 'No file selected'}
                </Typography>
                <DialogContentText>
                    The supplied SVG is sanitized and rendered to a stored 512×512 PNG. CSV fields
                    containing commas, line breaks, or quotes must use standard double-quote escaping.
                </DialogContentText>
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button onClick={props.onClose} disabled={busy}>Cancel</Button>
            <Button variant="contained" onClick={importTeams} disabled={!csv.trim() || busy}>
                {busy ? <CircularProgress size={20} /> : 'Import teams'}
            </Button>
        </DialogActions>
    </Dialog>;
}
